import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { surveyService } from "../services/surveyService";
import { responseSetService } from "../services/responseSetService";
import { useBodyClass } from "../hooks/useBodyClass";
import QuestionList from "../components/Survey/QuestionList";
import PlaceAutocomplete from "../components/Search/PlaceAutocomplete";

function SurveyPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [survey, setSurvey] = React.useState(null);
  const [questions, setQuestions] = React.useState([]);
  const [location, setLocation] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [formError, setFormError] = React.useState(null);
  const [showTips, setShowTips] = React.useState(false);

  useBodyClass("page-survey");

  React.useEffect(() => {
    surveyService.get(id).then((data) => {
      setSurvey(data);
      setQuestions(
        (data.questions || []).map((q) => ({
          ...q,
          error: false,
          answers: (q.answers || []).map((a) => ({ ...a, checked: false })),
        }))
      );
      setLoading(false);
    });
  }, [id]);

  function handleAnswerChange(questionId, answerId, type) {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== questionId) return q;
        return {
          ...q,
          error: false,
          answers: q.answers.map((a) => {
            if (type === "radio") {
              return { ...a, checked: a.id === answerId };
            }
            // checkbox: toggle
            if (a.id === answerId) {
              return { ...a, checked: !a.checked };
            }
            return a;
          }),
        };
      })
    );
  }

  function buildResponseSet() {
    const responses_attributes = [];
    questions.forEach((q) => {
      q.answers.forEach((a) => {
        if (a.checked) {
          responses_attributes.push({
            question_id: q.id,
            answer_id: a.id,
          });
        }
      });
    });
    return {
      survey_id: parseInt(id),
      name: survey?.name || "",
      location,
      responses_attributes,
    };
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);

    try {
      const responseSet = buildResponseSet();
      const result = await responseSetService.create(responseSet);
      // Full page redirect to confirmation page (rendered by Rails with ScreenerConfirmation)
      if (result.token) {
        window.location = `/assessments/response_sets/confirmation/${result.token}`;
      } else {
        window.location = "/";
      }
    } catch (err) {
      setFormError(true);
      // Mark specific questions with errors from server
      if (err?.error_message?.responses) {
        setQuestions((prev) =>
          prev.map((q) => {
            const serverError = err.error_message.responses.find(
              (r) => r.question_id === q.id
            );
            return { ...q, error: !!serverError };
          })
        );
      }
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="survey-container row">
        <div className="survey-content">
          <div className="loading-content">
            <div className="preloader">
              <div className="dot1" />
              <div className="dot2" />
            </div>
            <p className="direct">Loading</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-container row">
      <div className="survey-content">
        <form className="survey-form" onSubmit={handleSubmit}>
          <div className="introduction">
            <h1>{survey?.title || survey?.name}</h1>
            <div className="survey-description">
              <p>{survey?.description}</p>
              <a
                className="tips-show"
                onClick={() => setShowTips(!showTips)}
                role="button"
                tabIndex={0}
              >
                {showTips ? "Hide Tips and Tricks" : "View Tips and Tricks"}
              </a>
              {showTips && (
                <div className="tips">
                  <p>
                    Answer each question to the best of your ability. Your
                    responses will help us identify resources that match your
                    needs.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="questions">
            <QuestionList
              questions={questions}
              onAnswerChange={handleAnswerChange}
            />
          </div>

          <div className="location question">
            <div className="index">{questions.length + 1} of {questions.length + 1}</div>
            <h3 className="text">Where are you looking for services?</h3>
            <div className="answers">
              <div className="geolocate-wrap">
                <PlaceAutocomplete
                  hasTrailingControl
                  id="location"
                  name="location"
                  placeholder="Search address, city or zip"
                  required
                  value={location}
                  onChange={(val) => setLocation(val)}
                  onPlaceSelect={(val) => setLocation(val)}
                />
                <i className="geolocate ico ico-geo" />
              </div>
              <div className="location-label">
                <i className="icon-info" />
                Adding a location allows us to show you resources near you.
              </div>
            </div>
          </div>

          <div className="submit-label">
            If you have completed all the questions, submit your answers to see your results.
          </div>

          {formError && (
            <div className="survey-form-error">
              <div className="validation-error">
                <div className="error-wrapper">
                  <div className="alert-icon" />
                  <div className="message">Please answer all questions before continuing.</div>
                </div>
              </div>
            </div>
          )}

          <button
            type="submit"
            className="btn btn-secondary large survey-submit"
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default SurveyPage;
